import React from 'react';

import DataTable from '../shared/data_table';
import StatusPill from '../shared/status_pill';
import TeacherSlugLine from '../shared/teacher_slug_line';
import WhyPopover from '../overview/why_popover';
import RuleRefLink from '../rules_engine/rule_ref';
import { ROW_ACTION } from './params';

/*
 * PROD-7828 R6 — the month's penalties across teachers. Every button comes from the row's
 * editable.actions; the server's why_not says why the rest are missing (or why there are none). The money
 * columns are the row's money{} split (brief §4.2), so the table always agrees with the summary cards.
 * selectable=false (the session page, which has no bulk bar) drops the checkboxes.
 * on_error(e, text): a permission failure inside a rule-history dialog.
 */
export const ACTION_LABELS = {
  [ROW_ACTION.CORRECT]: 'Correct',
  [ROW_ACTION.WAIVE]: 'Waive',
  [ROW_ACTION.UNWAIVE]: 'Undo waiver',
  [ROW_ACTION.REVERT]: 'Revert',
  [ROW_ACTION.REMOVE]: 'Remove',
  [ROW_ACTION.APPLY_PASS]: 'Use free pass',
  [ROW_ACTION.REMOVE_PASS]: 'Remove free pass'
};

const PenaltyTable = ({
  rows,
  selectable = true,
  selected = {},
  on_toggle,
  on_toggle_all,
  on_action,
  highlight_uuid,
  empty_text,
  sort,
  on_sort,
  on_error
}) => {
  // why_not also explains a partial set (a pass-covered row offers only the pass removal).
  const actions_cell = (r) =>
    r.actions.length ? (
      <span>
        <span className="tp-actions" data-actions-for={r.uuid}>
          {r.actions.map((a) => (
            <button
              key={a}
              type="button"
              className="tp-link"
              aria-label={`${ACTION_LABELS[a]} ${r.short_id}`}
              onClick={() => on_action(a, r)}
            >
              {ACTION_LABELS[a]}
            </button>
          ))}
        </span>
        {r.why_not ? <div className="tp-card-field-label">{r.why_not}</div> : null}
      </span>
    ) : (
      <span className="tp-card-field-label">{r.why_not || 'No changes allowed'}</span>
    );

  const all_on = rows.length > 0 && rows.every((r) => selected[r.uuid]);
  const select_cell = (r) =>
    selectable ? (
      <input
        type="checkbox"
        aria-label={`Select ${r.short_id}`}
        checked={!!selected[r.uuid]}
        onChange={() => on_toggle(r)}
      />
    ) : null;

  const select_column = {
    key: 'select',
    label: (
      <input
        type="checkbox"
        aria-label="Select every listed penalty"
        checked={all_on}
        onChange={() => on_toggle_all(!all_on)}
      />
    ),
    render: select_cell,
    hideInCard: true
  };

  const columns = [
    ...(selectable ? [select_column] : []),
    {
      key: 'short_id',
      label: 'Penalty',
      render: (r) => <span className="tp-mono">{r.short_id}</span>
    },
    {
      key: 'teacher_name',
      label: 'Teacher',
      hideInCard: true,
      sort_key: 'teacher_name',
      render: (r) => (
        <div>
          <div>{r.teacher_name}</div>
          <TeacherSlugLine slug={r.teacher_slug} />
        </div>
      )
    },
    { key: 'session_text', label: 'Session (IST)', sort_key: 'session_time' },
    {
      key: 'student_name',
      label: 'Student',
      sort_key: 'student_name',
      // A month-level penalty (session_uuid '') has no session page to open.
      render: (r) =>
        r.session_uuid ? (
          <a
            className="tp-link"
            href={`/sessiondetails?id=${r.session_uuid}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {r.student_name}
          </a>
        ) : (
          r.student_name
        )
    },
    {
      key: 'type_text',
      label: 'Type',
      sort_key: 'type',
      render: (r) => (
        <span>
          {r.type_text}
          <RuleRefLink rule_ref={r.rule_ref} on_error={on_error} />
          {r.is_manual ? <div className="tp-card-field-label">Added by Ops</div> : null}
          {(r.join_lines || []).map((l) => (
            <div key={l.label} className="tp-pe-join-time" data-join-time={l.label}>
              {l.label}: {l.value}
            </div>
          ))}
        </span>
      )
    },
    { key: 'base_rate_text', label: 'Base rate', align: 'right', sort_key: 'base_rate' },
    { key: 'pct_text', label: 'Pen %', align: 'right', sort_key: 'pct' },
    {
      key: 'original_text',
      label: 'Original',
      align: 'right',
      sort_key: 'original',
      render: (r) => (
        <span>
          {r.original_text}
          {r.corrected_text ? (
            <div className="tp-card-field-label">Corrected {r.corrected_text}</div>
          ) : null}
        </span>
      )
    },
    {
      key: 'charged_text',
      label: 'Charged',
      align: 'right',
      sort_key: 'charged',
      render: (r) => (
        <WhyPopover
          detail={r.explain}
          amount_node={<span className={r.charged ? 'tp-neg' : ''}>{r.charged_text}</span>}
        />
      )
    },
    {
      key: 'waiver_text',
      label: 'Status',
      sort_key: 'status',
      render: (r) => (
        <span>
          <StatusPill tone={r.waiver_tone}>{r.waiver_text}</StatusPill>
          {r.pass_text ? <div className="tp-card-field-label">Free pass {r.pass_text}</div> : null}
          {r.waived_text ? <div className="tp-card-field-label">Waived {r.waived_text}</div> : null}
          {r.state_text ? (
            <div className="tp-card-field-label">
              {r.state_text}
              {r.uncharged_orphan_text ? ` (${r.uncharged_orphan_text})` : ''}
            </div>
          ) : null}
          {r.needs_review ? <StatusPill tone="orange">Month needs review</StatusPill> : null}
        </span>
      )
    },
    {
      key: 'note',
      label: 'Reason / note',
      render: (r) => (
        <span title={r.updated_by_title}>
          {r.reason_text ? <div>{r.reason_text}</div> : null}
          {r.note || '—'}
          {r.approved_by ? (
            <div className="tp-card-field-label">Approved by {r.approved_by}</div>
          ) : null}
          {r.updated_text ? <div className="tp-card-field-label">{r.updated_text}</div> : null}
        </span>
      )
    },
    { key: 'actions', label: 'Actions', hideInCard: true, render: actions_cell }
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.key}
      rowClassName={(r) => (highlight_uuid && r.uuid === highlight_uuid ? 'tp-row-hl' : '')}
      sort={sort}
      on_sort={on_sort}
      emptyText={empty_text}
      cardTitle={(r) => (
        <span>
          {select_cell(r)} {r.teacher_name}
          <span className={`${r.charged ? 'tp-neg' : ''} tp-card-amt`}>{r.charged_text}</span>
          {actions_cell(r)}
        </span>
      )}
    />
  );
};

export default PenaltyTable;
